/**
 * Server functions for CIH training programs (CTPs).
 *
 * CTPs are training program content pages managed by admins.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: ctps, cih ctps, training programs, ctp crud, ctp lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  createCtpInputSchema,
  updateCtpInputSchema,
  type CihCtpListRow,
  type CihCtpRow,
  type CtpChallengeRow,
  type CtpFrameworkRow,
} from "~/lib/server/cih/ctps";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type {
  CihCtpListRow,
  CihCtpRow,
  CtpChallengeRow,
  CtpFrameworkRow,
} from "~/lib/server/cih/ctps";

// ============================================================================
// Query Keys
// ============================================================================

export const ctpQueryKeys = {
  all: ["ctps"] as const,
  list: (status?: string) => ["ctps", "list", status ?? "all"] as const,
  detail: (id: string) => ["ctps", id] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * List all CTPs (admin). Excludes draft-of rows by default.
 * Optionally filter by status.
 */
export const getCihCtpsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ status: z.string().optional() }).optional())
  .handler(async ({ data }): Promise<CihCtpListRow[]> => {
    const { listCtps } = await import("~/lib/server/cih/ctps");
    return listCtps(data);
  });

/**
 * Get a single CTP by ID (admin) with all fields.
 */
export const getCihCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<CihCtpRow | null> => {
    const { getCtp } = await import("~/lib/server/cih/ctps");
    return getCtp(data);
  });

/**
 * Update CTP fields (admin, partial update).
 */
export const updateCihCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updateCtpInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateCtp } = await import("~/lib/server/cih/ctps");
    return updateCtp(data);
  });

/**
 * Update CTP fields and its accreditor set in one transaction (admin).
 * Validates the owner and links as they will be, so an owner/accreditor swap
 * is expressible and a failure cannot half-apply.
 */
export const updateCihCtpWithAccreditorsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      update: updateCtpInputSchema,
      ccoIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateCtpWithAccreditors } = await import("~/lib/server/cih/ctps");
    return updateCtpWithAccreditors(data);
  });

/**
 * Admin self-publish a draft CTP (skips review).
 */
export const publishCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { publishCtp } = await import("~/lib/server/cih/ctps");
    return publishCtp(data, { userId: context.user.id });
  });

/**
 * Archive a published CTP.
 */
export const archiveCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { archiveCtp } = await import("~/lib/server/cih/ctps");
    return archiveCtp(data, { userId: context.user.id });
  });

/**
 * Create an editable draft of a published CTP.
 */
export const createCtpDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<CihCtpRow> => {
    const { createDraftOfPublishedCtp } = await import("~/lib/server/cih/ctps");
    return createDraftOfPublishedCtp(data, { userId: context.user.id });
  });

/**
 * Promote a draft back onto its published parent, recorded in the page
 * change history.
 */
export const promoteCtpDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ publishedId: string }> => {
    const { publishDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    return publishDraftAsStaff({
      kind: "program",
      id: data.draftId,
      actorUserId: context.user.id,
      via: "promote",
    });
  });

/**
 * Restore an archived CTP to draft.
 */
export const restoreCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { restoreCtp } = await import("~/lib/server/cih/ctps");
    return restoreCtp(data, { userId: context.user.id });
  });

/**
 * Approve an in_review CTP (publish it).
 */
export const approveCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ publishedId: string }> => {
    const { publishDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    return publishDraftAsStaff({
      kind: "program",
      id: data.id,
      actorUserId: context.user.id,
      via: "approve",
    });
  });

/**
 * Return an in_review CTP to draft with feedback.
 */
export const returnCtpToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { returnDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    await returnDraftAsStaff({
      kind: "program",
      id: data.id,
      actorUserId: context.user.id,
      note: data.note,
    });
    return { success: true };
  });

/**
 * Create a new CTP as draft with minimal fields.
 * Returns the created row so the caller can navigate to the editor.
 */
export const createCihCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createCtpInputSchema)
  .handler(async ({ data, context }): Promise<CihCtpRow> => {
    const { createCtp } = await import("~/lib/server/cih/ctps");
    return createCtp(data, { userId: context.user.id });
  });

/**
 * Get frameworks taught by a CTP (by CTP ID).
 * Used by the CTP detail page's FrameworkCards section.
 */
export const getCtpFrameworksFn = createServerFn({ method: "GET" })
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(async ({ data }): Promise<CtpFrameworkRow[]> => {
    const { getCtpFrameworks } = await import("~/lib/server/cih/ctps");
    return getCtpFrameworks(data);
  });

/**
 * Get the organizations that accredit a CTP (public). Distinct from the
 * program's own ccoId, which is the school that delivers it.
 */
export const getCtpAccreditorsFn = createServerFn({ method: "GET" })
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(
    async ({
      data,
    }): Promise<{ id: string; slug: string | null; name: string }[]> => {
      const { getCtpAccreditors } = await import("~/lib/server/cih/ctps");
      return getCtpAccreditors(data);
    },
  );

/**
 * Get challenges that use any framework taught by this CTP.
 * Path: ctpFrameworks → challengeFrameworks → challenges (published, deduped).
 */
export const getChallengesByCtpFn = createServerFn({ method: "GET" })
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(async ({ data }): Promise<CtpChallengeRow[]> => {
    const { getChallengesByCtp } = await import("~/lib/server/cih/ctps");
    return getChallengesByCtp(data);
  });

/**
 * Get framework IDs linked to a CTP (admin). Used by the CTP editor's
 * Frameworks Taught panel.
 */
export const getCtpFrameworkIdsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getCtpFrameworkIds } = await import("~/lib/server/cih/ctps");
    return getCtpFrameworkIds(data);
  });

/**
 * Sync framework links for a CTP (admin).
 * Computes diff: deletes removed, inserts new.
 */
export const syncCtpFrameworksFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      ctpId: z.string().uuid(),
      frameworkIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { syncCtpFrameworks } = await import("~/lib/server/cih/ctps");
    return syncCtpFrameworks(data);
  });

/**
 * Get the IDs of the organizations that accredit a CTP (admin). Used by the
 * CTP editor's Accredited By panel.
 */
export const getCtpAccreditorIdsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getCtpAccreditorIds } = await import("~/lib/server/cih/ctps");
    return getCtpAccreditorIds(data);
  });

/**
 * List CTPs belonging to a CCO (admin). Used by the CCO editor's reverse-lookup panel.
 */
export const getCtpsByCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ccoId: z.string().uuid() }))
  .handler(async ({ data }): Promise<CihCtpListRow[]> => {
    const { listCtps } = await import("~/lib/server/cih/ctps");
    return listCtps({ ccoId: data.ccoId });
  });

/**
 * Count coaches who have a certification in any CTP belonging to this CTP ID.
 * Returns a plain count for summary display.
 */
export const getCtpCoachCountFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ctpId: z.string().uuid() }))
  .handler(async ({ data }): Promise<number> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCtps } = await import("~/../drizzle/schema");
    const { count, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [result] = await db
      .select({ total: count() })
      .from(coachesCtps)
      .where(eq(coachesCtps.ctpId, data.ctpId));
    return result?.total ?? 0;
  });

// ============================================================================
// Query Options
// ============================================================================

export const ctpsQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: ctpQueryKeys.list(status),
    queryFn: () => getCihCtpsFn({ data: { status } }),
  });

export const ctpQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ctpQueryKeys.detail(id),
    queryFn: () => getCihCtpFn({ data: { id } }),
  });
